/**
 * The single place the dashboard decides what a detection result "says".
 *
 * A result carries two answers once manual resolution exists: what the engine
 * concluded (`type`) and, when someone resolved an inconclusive one, what they
 * concluded (`manualOverride.outcome`). Every surface that renders or counts a
 * detection result must ask this rather than reading `type` directly — the
 * failure mode otherwise is one screen showing a result as resolved while
 * another still shows it inconclusive.
 *
 * Mirrors src/miscellaneous/detection-outcome.ts in quik-core-v2 and
 * lib/utils/detection_outcome.dart in quik-connect-v2. Keep the three in step.
 */

export type DetectionOutcome = 'true' | 'false' | 'other';

export interface DetectionResultLike {
  type?: string;
  manualOverride?: { outcome?: string | null } | null;
}

/** The outcome to act on and display. A manual resolution wins when present. */
export function detectionOutcome(result: DetectionResultLike | null | undefined): DetectionOutcome {
  const overridden = result?.manualOverride?.outcome;
  if (overridden === 'true' || overridden === 'false') return overridden;

  const raw = result?.type;
  if (raw === 'true' || raw === 'false' || raw === 'other') return raw;
  return 'other';
}

/** True when a human resolved this result rather than the engine deciding it. */
export function isManuallyResolved(result: DetectionResultLike | null | undefined): boolean {
  const outcome = result?.manualOverride?.outcome;
  return outcome === 'true' || outcome === 'false';
}

/**
 * Only inconclusive results may be resolved by hand. A confidently-wrong
 * verdict is a rule defect — fixing it at the result would turn the detection
 * log into a record of opinion.
 */
export function canManuallyResolve(result: DetectionResultLike | null | undefined): boolean {
  return result?.type === 'other';
}

export const DETECTION_OUTCOME_LABEL: Record<DetectionOutcome, string> = {
  true: 'Match',
  false: 'No Match',
  other: 'Inconclusive'
};

export const DETECTION_OUTCOME_SEVERITY: Record<DetectionOutcome, 'success' | 'error' | 'warning'> = {
  true: 'success',
  false: 'error',
  other: 'warning'
};

/** "claude-sonnet-5-5" → "Claude Sonnet 5.5", for showing which model decided. */
export const formatModelName = (id?: string): string => {
  if (!id) return 'A stronger model';
  const m = String(id).match(/^claude-([a-z]+)-(\d+)(?:-(\d+))?$/);
  if (!m) return id;
  const family = m[1].charAt(0).toUpperCase() + m[1].slice(1);
  return `Claude ${family} ${m[2]}${m[3] ? `.${m[3]}` : ''}`;
};
